'use client';
import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from 'react';

interface Track {
  id: string;
  title: string;
  artist: string;
  duration: string;
  year: string;
  cover: string;
  coverAlt: string;
  audioUrl: string | null;
}

interface MusicPlayerContextValue {
  playlist: Track[];
  currentIndex: number;
  track: Track | undefined;
  isPlaying: boolean;
  progress: number;
  duration: number;
  currentTime: number;
  volume: number;
  muted: boolean;
  shuffle: boolean;
  repeat: boolean;
  expanded: boolean;
  liked: string[];
  audioError: string | null;
  loadingTracks: boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
  setCurrentIndex: (i: number) => void;
  setVolume: (v: number) => void;
  setMuted: (m: boolean) => void;
  setShuffle: (s: boolean) => void;
  setRepeat: (r: boolean) => void;
  setExpanded: (e: boolean) => void;
  setAudioError: (e: string | null) => void;
  handlePlayPause: () => void;
  handleNext: () => void;
  handlePrev: () => void;
  handleSeek: (pct: number) => void;
  toggleLike: (id: string) => void;
  playTrackAt: (index: number) => void;
}

const MusicPlayerContext = createContext<MusicPlayerContextValue | null>(null);

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  return ctx;
}

export function MusicPlayerProvider({ children }: { children: React.ReactNode }) {
  const [playlist, setPlaylist] = useState<Track[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(0.75);
  const [muted, setMuted] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [liked, setLiked] = useState<string[]>([]);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [loadingTracks, setLoadingTracks] = useState(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Load tracks from DB ONCE — this provider lives at the root layout,
  // so it survives client-side navigation between pages.
  useEffect(() => {
    setLoadingTracks(true);
    fetch('/api/music/tracks')
      .then((r) => (r.ok ? r.json() : { tracks: [] }))
      .then((data) => {
        if (data?.tracks && data.tracks.length > 0) {
          setPlaylist(data.tracks);
        }
      })
      .catch(() => {})
      .finally(() => setLoadingTracks(false));
  }, []);

  const track = playlist[currentIndex];

  // Sync audio element when track changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    setAudioError(null);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);

    if (track?.audioUrl) {
      audio.src = track.audioUrl;
      audio.load();
      if (isPlaying) {
        audio.play().catch((e) => {
          setAudioError('Playback failed: ' + e.message);
          setIsPlaying(false);
        });
      }
    } else {
      audio.src = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, playlist]);

  // Sync volume/mute
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = muted ? 0 : volume;
  }, [volume, muted]);

  const handlePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (!track?.audioUrl) {
      setAudioError('No audio file available for this track. Upload audio in the admin panel.');
      return;
    }

    setAudioError(null);
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => {
        setIsPlaying(true);
      }).catch((e) => {
        setAudioError('Playback failed: ' + e.message);
        setIsPlaying(false);
      });
    }
  }, [isPlaying, track]);

  const handleNext = useCallback(() => {
    setAudioError(null);
    if (shuffle) {
      const next = Math.floor(Math.random() * playlist.length);
      setCurrentIndex(next);
    } else {
      setCurrentIndex((prev) => (prev + 1) % playlist.length);
    }
  }, [shuffle, playlist.length]);

  const handlePrev = useCallback(() => {
    setAudioError(null);
    const audio = audioRef.current;
    if (audio && audio.currentTime > 3) {
      audio.currentTime = 0;
      return;
    }
    setCurrentIndex((prev) => (prev - 1 + playlist.length) % playlist.length);
  }, [playlist.length]);

  const handleSeek = useCallback((pct: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = pct * duration;
  }, [duration]);

  const toggleLike = useCallback((id: string) => {
    setLiked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const playTrackAt = useCallback((index: number) => {
    setCurrentIndex(index);
    setAudioError(null);
    setTimeout(() => {
      const audio = audioRef.current;
      const t = playlist[index];
      if (audio && t?.audioUrl) {
        audio.play().catch(() => {});
        setIsPlaying(true);
      }
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist]);

  const value = useMemo<MusicPlayerContextValue>(() => ({
    playlist, currentIndex, track, isPlaying, progress, duration, currentTime,
    volume, muted, shuffle, repeat, expanded, liked, audioError, loadingTracks, audioRef,
    setCurrentIndex, setVolume, setMuted, setShuffle, setRepeat, setExpanded, setAudioError,
    handlePlayPause, handleNext, handlePrev, handleSeek, toggleLike, playTrackAt,
  }), [
    playlist, currentIndex, track, isPlaying, progress, duration, currentTime,
    volume, muted, shuffle, repeat, expanded, liked, audioError, loadingTracks,
    handlePlayPause, handleNext, handlePrev, handleSeek, toggleLike, playTrackAt,
  ]);

  return (
    <MusicPlayerContext.Provider value={value}>
      <audio
        ref={audioRef}
        onTimeUpdate={() => {
          const audio = audioRef.current;
          if (!audio) return;
          setCurrentTime(audio.currentTime);
          setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
        }}
        onDurationChange={() => {
          const audio = audioRef.current;
          if (audio) setDuration(audio.duration);
        }}
        onEnded={() => {
          if (repeat) {
            const audio = audioRef.current;
            if (audio) { audio.currentTime = 0; audio.play().catch(() => {}); }
          } else {
            handleNext();
          }
        }}
        onError={() => {
          setAudioError('Audio file could not be loaded.');
          setIsPlaying(false);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        preload="metadata"
      />
      {children}
    </MusicPlayerContext.Provider>
  );
}
